import { ClassAttributes, SetStateAction, TdHTMLAttributes, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import Button from '@/components/Button'
import Card from '@/components/Card'
import { Plus } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'
import Modal from '@/pages/OrganizationChannelConnectionsPage/CreateChannelConnection/CreateChannelConnectionModal'
import { ChannelConnection, ChannelConnections } from '@/pages/OrganizationChannelConnectionsPage/interfaces'
import sharedMessages from '@/pages/OrganizationChannelConnectionsPage/messages'

import columns from './columns'
import messages from './messages'

const StyledCard = styled(Card)`
  padding-top: 0;
  padding-left: 20px;
  padding-right: 20px;
  box-shadow: none;
  border: none;
`

const StyledCardHeader = styled(Card.Header)`
  flex-direction: column;
  .card__title {
    margin-top: 0;
    margin-bottom: 0;
    flex: 1 1;
    font-size: 1.4rem;
    font-weight: normal;
  }
`

const StyledFlexContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  justify-content: space-between;
  margin-right: 2rem;
`

const StyledLink = styled.a`
  margin-left: 0.25rem;
  font-size: 13px;
`

const StyledPaginationTable = styled(PaginationTable)`
  @media (max-width: 1050px) {
    td:nth-of-type(3),
    th:nth-of-type(3),
    td:nth-of-type(4),
    th:nth-of-type(4) {
      display: none;
    }
  }
`

// table sort
const defaultSort = {
  modifiedDate: {
    direction: 'desc',
    position: 0
  },
  name: {
    direction: 'asc',
    position: 1
  }
}

interface SummaryProps {
  channelConnections: ChannelConnections[]
  onDeleteChannel: (id: string) => void
  onCreateChannel: () => void
  clearSelectedChannel: () => void
  onSelectChannel: (channelConnection: ChannelConnection) => void
  setConfirmDelete: object
  currentChannelConnection: ChannelConnection
  onEditChannelConnection: (channelConnection: ChannelConnection) => void
}

const Summary = ({
  channelConnections,
  onDeleteChannel,
  onCreateChannel,
  setConfirmDelete,
  onSelectChannel,
  clearSelectedChannel,
  currentChannelConnection,
  onEditChannelConnection
}: SummaryProps) => {
  const intl = useIntl()

  const [displayedConnections, setDisplayedConnections] = useState<ChannelConnections[]>()
  const [currentPage, setCurrentPage] = useState(1)
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    setDisplayedConnections(channelConnections)
  }, [channelConnections])

  const CellWrapper = (
    cellProps: JSX.IntrinsicAttributes &
      ClassAttributes<HTMLTableDataCellElement> &
      TdHTMLAttributes<HTMLTableDataCellElement>
  ) => {
    return <td className="apps-table__cell" {...cellProps} />
  }

  const onFilterChange = () => {}

  const onEditCancel = () => {
    clearSelectedChannel()
    setShowEditModal(false)
  }

  const onEditConfirm = (values: any) => {
    onEditChannelConnection(values)
  }

  const editChannelConnection = (channelConnection: ChannelConnection) => {
    onSelectChannel(channelConnection)
    setShowEditModal(true)
  }

  return (
    <StyledCard>
      <StyledCardHeader>
        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-2">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              <FormattedMessage {...messages.title} />
            </h2>
            <StyledFlexContainer>
              <p>
                <FormattedMessage {...sharedMessages.needHelpDescription} />
                <StyledLink
                  href={'https://help.workgrid.com/s/article/Understanding-Platforms-and-Devices'}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage {...sharedMessages.needHelpLink} />
                </StyledLink>
              </p>
            </StyledFlexContainer>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Button color="primary" onClick={onCreateChannel}>
              <Plus />
              <FormattedMessage {...messages.createChannel} />
            </Button>
          </div>
        </div>
      </StyledCardHeader>
      <Card.Content>
        <Modal
          onSubmit={onEditConfirm}
          onClose={onEditCancel}
          isOpen={showEditModal}
          isEditing={showEditModal}
          formValues={currentChannelConnection}
        />
        <StyledPaginationTable
          columns={columns({
            intl,
            onSelectChannel: editChannelConnection,
            onFilterChange,
            onDeleteChannel,
            setConfirmDelete
          })}
          onPaginateSelect={(page: SetStateAction<number>) => setCurrentPage(page)}
          pagination={{
            isClientPaging: true,
            count: displayedConnections ? displayedConnections.length : 0,
            startIndex: currentPage * 10,
            perPage: 10
          }}
          renderers={{
            body: {
              cell: CellWrapper
            }
          }}
          rowItemType=""
          rows={displayedConnections}
          rowKey="_index"
          sortingColumn={defaultSort}
        />
      </Card.Content>
    </StyledCard>
  )
}

Summary.displayName = 'Summary'
export default Summary
